import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { xxHashObject } from '@flakiness/shared/common/utils.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { hrefOrClick, Linkifier } from '../linkifier.js';
import { outcomeSwatchFill } from './outcome-legend.js';

// A test that did not report in one of the periods gets its own row and column, and has no
// outcome there, as in `WireTypes.OutcomeTransition`.
type Axis = WireTypes.AnalyticsOutcome | undefined;
const ROWS: Axis[] = [...WireTypes.ANALYTICS_OUTCOMES, undefined];
const COLUMNS: Axis[] = [...WireTypes.ANALYTICS_OUTCOMES, undefined];

// A selection is a union of cells, so the order they come in does not matter.
function isSameTransitions(one: WireTypes.TransitionFilter[]|undefined, other: WireTypes.TransitionFilter[]|undefined): boolean {
  if (!one || !other)
    return !one && !other;
  if (one.length !== other.length)
    return false;
  const hashes = (filters: WireTypes.TransitionFilter[]) => filters.map(filter => xxHashObject(filter)).sort();
  const otherHashes = hashes(other);
  return hashes(one).every((hash, index) => hash === otherHashes[index]);
}

// A cell reads as a sentence: "was passing" x "now flaky".
const AXIS_STATES: Record<WireTypes.AnalyticsOutcome, string> = {
  expected: 'passing',
  unexpected: 'failing',
  flaked: 'flaky',
  skipped: 'skipped',
};

// Off-diagonal cells take their color from the outcome the tests moved into.
const CELL_TONES: Record<WireTypes.AnalyticsOutcome, string> = {
  expected: 'passing',
  unexpected: 'failing',
  flaked: 'flaky',
  skipped: 'muted',
};

/**
 * Every transition between two periods as a matrix: rows are the outcome in the selected
 * period, columns the outcome in the previous one. The diagonal is tests that kept their outcome.
 */
@customElement('outcome-matrix')
class OutcomeMatrix extends LitElement {
  @property({ attribute: false }) transitions?: WireTypes.OutcomeTransition[];
  @property({ attribute: false }) beforeLabel?: string;
  @property({ attribute: false }) afterLabel?: string;
  /** The cells whose tests are listed below the matrix, if any. */
  @property({ attribute: false }) selected?: WireTypes.TransitionFilter[];
  /**
   * Where picking a cell, a row or a column leads, given the cells it picks. Picking what is
   * already selected clears the selection, and then the cells are `undefined`.
   */
  @property({ attribute: false }) linkifier?: Linkifier<WireTypes.TransitionFilter[]|undefined>;

  render() {
    const transitions = this.transitions;
    if (!transitions)
      return nothing;

    const count = (from: Axis, to: Axis) => transitions
      .filter(t => t.from === from && t.to === to)
      .reduce((sum, t) => sum + t.count, 0);
    return html`
      <div class=after-axis style=${styleMap({ 'grid-column': '1', 'grid-row': `3 / span ${ROWS.length}` })}>
        <span>${this.afterLabel}</span>
      </div>

      <div></div>
      <div></div>
      <div class=before-axis style=${styleMap({ 'grid-column': `span ${COLUMNS.length}` })}>
        Compared with ${this.beforeLabel}
      </div>

      <div></div>
      <div></div>
      ${COLUMNS.map((from, column) => this._renderHead(from, 'from', {
        'last-column': column === COLUMNS.length - 1,
      }))}

      ${ROWS.map((to, row) => html`
        ${this._renderHead(to, 'to', {
          'row-head': true,
          'last-row': row === ROWS.length - 1,
        })}
        ${COLUMNS.map((from, column) => {
          // A test cannot be missing from both periods, so the corner where the two reporting
          // axes meet holds nothing. It keeps the top and left lines every cell draws, which are
          // what close the frame around the notch, but has no content, colour or click.
          if (from === undefined && to === undefined)
            return html`<div class=cell></div>`;
          return this._renderCell(from, to, count(from, to), {
            'last-row': row === ROWS.length - 1,
            'last-column': column === COLUMNS.length - 1,
          });
        })}
      `)}
    `;
  }

  // Picking what is already picked clears it, so a cell toggles.
  private _link(filters: WireTypes.TransitionFilter[]) {
    return this.linkifier?.(isSameTransitions(this.selected, filters) ? undefined : filters);
  }

  // Every cell the selection covers is tinted: a whole row, a whole column, or one cell.
  private _isInSelection(from: Axis, to: Axis): boolean {
    return (this.selected ?? []).some(filter => filter.from === from && filter.to === to);
  }

  /**
   * A row or column header. Picking one selects everything on that axis: a column is every test
   * that had the outcome in the previous period, a row is every test that has it now.
   */
  private _renderHead(outcome: Axis, direction: 'from' | 'to', classes: ClassInfo) {
    const label = this._renderAxisLabel(outcome, direction);
    // A column is the tests that had this outcome before and still report now: a test that is
    // gone has nothing to show in the selected period, so it is left to its own row. A row is
    // every cell in it, except that no test is missing from both periods.
    const filters: WireTypes.TransitionFilter[] = direction === 'from'
      ? WireTypes.ANALYTICS_OUTCOMES.map(to => ({ from: outcome, to }))
      : (outcome === undefined ? WireTypes.ANALYTICS_OUTCOMES : COLUMNS).map(from => ({ from, to: outcome }));
    return html`
      <a
        class=${classMap({
          'head': true,
          'clickable': true,
          ...classes,
          'selected': isSameTransitions(this.selected, filters),
        })}
        ${hrefOrClick(this._link(filters))}
      >${label}</a>
    `;
  }

  private _renderAxisLabel(outcome: Axis, direction: 'from' | 'to') {
    if (outcome === undefined)
      return direction === 'from' ? 'Was not reported' : 'No longer reported';
    return html`
      <span class=dot style=${styleMap({ background: outcomeSwatchFill(outcome) })}></span>
      ${direction === 'from' ? 'Was' : 'Now'} ${AXIS_STATES[outcome]}
    `;
  }

  private _renderCell(from: Axis, to: Axis, value: number, edges: ClassInfo) {
    // The diagonal is what stayed put, so it recedes whether or not anything is there.
    // A cell with nothing in it has nothing to color either.
    const changed = from !== to && value > 0;
    // A cell picks exactly the tests that moved that way. An empty one is still worth picking:
    // the answer is simply that no test made that move.
    const filters: WireTypes.TransitionFilter[] = [{ from, to }];
    return html`
      <a
        class=${classMap({
          'cell': true,
          'clickable': true,
          ...edges,
          'same': from === to,
          'changed': changed,
          [to ? CELL_TONES[to] : 'muted']: changed,
          'in-selection': this._isInSelection(from, to),
          'selected': isSameTransitions(this.selected, filters),
        })}
        ${hrefOrClick(this._link(filters))}
      >${this._renderCount(value)}</a>
    `;
  }

  private _renderCount(value: number) {
    return value ? value.toLocaleString() : html`<span class=none>—</span>`;
  }

  static styles = css`
    :host {
      display: grid;
      /* The period label and the row names hug their text; the data columns share the rest. */
      grid-template-columns: max-content max-content repeat(5, auto);
      font-size: var(--sl-font-size-small);
      overflow-x: auto;
    }

    /* The selected period runs up the left edge, the previous one across the top. */
    .after-axis {
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--fk-color-border);
      border-radius: var(--sl-border-radius-large) 0 0 var(--sl-border-radius-large);
      padding: var(--sl-spacing-large) var(--sl-spacing-x-small);
    }

    /* Only the text turns, so the cell keeps its borders and padding upright. */
    .after-axis span {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
    }

    /* Boxed, so the period caption reads as a label of the table rather than part of it. */
    .before-axis {
      text-align: center;
      border: 1px solid var(--fk-color-border);
      border-radius: var(--sl-border-radius-large) var(--sl-border-radius-large) 0 0;
      padding: var(--sl-spacing-x-small) var(--sl-spacing-large);
    }

    /* The periods are what the matrix compares, so they read above the row and column names. */
    .after-axis, .before-axis {
      font-weight: var(--sl-font-weight-semibold);
      color: var(--sl-color-neutral-700);
    }

    /* Headers and cells are links, but keep the colours their own classes give them. */
    a {
      color: inherit;
      text-decoration: none;
    }

    .head {
      color: var(--sl-color-neutral-700);
      text-align: end;
      white-space: nowrap;
      padding: var(--sl-spacing-x-small) var(--sl-spacing-large);
    }

    .row-head {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      justify-content: start;
      padding: var(--sl-spacing-x-small) var(--sl-spacing-large);
      border-top: 1px solid var(--fk-color-border);
    }

    .cell {
      text-align: end;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      padding: var(--sl-spacing-x-small) var(--sl-spacing-large) var(--sl-spacing-x-small) 0;
      border-top: 1px solid var(--fk-color-border);
    }

    /* The table's own edges: rows and columns only draw their top and left lines. */
    .last-column {
      border-right: 1px solid var(--fk-color-border);
    }

    .last-row {
      border-bottom: 1px solid var(--fk-color-border);
    }

    /* Column dividers, but not between the period label and the row names. */
    .head:not(.row-head), .cell {
      border-left: 1px solid var(--fk-color-border);
      padding-left: var(--sl-spacing-large);
    }

    /* Tests that kept their outcome sit on the diagonal and are not news. */
    .same {
      color: var(--sl-color-neutral-400);
      background: var(--sl-color-neutral-50);
    }

    .changed {
      font-weight: var(--sl-font-weight-semibold);
    }

    .clickable {
      cursor: pointer;
    }

    .clickable:hover {
      text-decoration: underline;
    }

    .clickable:focus-visible {
      outline: var(--sl-focus-ring);
      outline-offset: calc(-1 * var(--sl-focus-ring-width));
    }

    .passing {
      color: var(--sl-color-success-700);
      background: var(--sl-color-success-50);
    }

    .failing {
      color: var(--sl-color-danger-600);
      background: var(--sl-color-danger-50);
    }

    .flaky {
      color: var(--sl-color-warning-700);
      background: var(--sl-color-warning-50);
    }

    .muted {
      color: var(--sl-color-neutral-500);
    }

    /* A selected row or column takes over the cells' own colours, so the band reads as one.
       Declared after them, since they are single-class rules too. */
    .in-selection {
      background: var(--sl-color-primary-50);
    }

    /* The cell or header that was actually picked, inside the selected row or column. */
    .selected {
      background: var(--sl-color-primary-50);
      box-shadow: inset 0 0 0 2px var(--sl-color-primary-500);
    }

    .none {
      color: var(--sl-color-neutral-400);
    }

    .dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 2px;
    }
  `;
}
